import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import { sortCustomOrderStatuses } from '../../types/OrderStatusTypes';
import '../business/Business.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';
import './UpsertCustomOrderStatus.css';

const STATUS_FLOW = [
  { key: 'created', label: 'новое' },
  { key: 'processed', label: 'принят' },
  { key: 'custom', label: 'ваши этапы' },
  { key: 'completed', label: 'выполнено' },
  { key: 'canceled', label: 'отменено' },
];

function UpsertCustomOrderStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const { customOrderStatusId } = useParams();
  const isEdit = !!customOrderStatusId;

  const { loading, requestWithMeta } = useHttp();

  const [name, setName] = React.useState('');
  const [submitError, setSubmitError] = React.useState(null);
  const [isPrefillLoading, setIsPrefillLoading] = React.useState(false);

  const listPath = '/order-statuses';

  React.useEffect(() => {
    let cancelled = false;
    if (!isEdit) return;

    setSubmitError(null);

    async function prefill() {
      setIsPrefillLoading(true);
      try {
        const stateStatus = location?.state?.customOrderStatus ?? null;
        const stateId = stateStatus?.id ?? stateStatus?._id ?? null;
        if (stateStatus && stateId && String(stateId) === String(customOrderStatusId)) {
          if (cancelled) return;
          setName(stateStatus?.name ?? '');
          return;
        }

        const res = await requestWithMeta('/operator/custom-order-statuses', 'GET');
        if (!res?.ok) {
          if (cancelled) return;
          setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить этап');
          return;
        }

        const list = sortCustomOrderStatuses(
          Array.isArray(res?.data?.customOrderStatuses) ? res.data.customOrderStatuses : []
        );
        const status = list.find(
          (item) => String(item?.id ?? item?._id) === String(customOrderStatusId)
        );
        if (!status) {
          if (cancelled) return;
          setSubmitError('Этап не найден');
          return;
        }

        if (cancelled) return;
        setName(status?.name ?? '');
      } finally {
        if (!cancelled) setIsPrefillLoading(false);
      }
    }

    prefill();
    return () => {
      cancelled = true;
    };
  }, [isEdit, customOrderStatusId, location?.state, requestWithMeta]);

  const isSubmitDisabled =
    loading || isPrefillLoading || name.trim().length === 0;

  async function handleSubmit() {
    setSubmitError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setSubmitError('Введите название этапа');
      return;
    }

    if (isEdit) {
      const res = await requestWithMeta(
        `/operator/custom-order-statuses/${encodeURIComponent(customOrderStatusId)}`,
        'PUT',
        { name: trimmedName }
      );
      if (!res?.ok) {
        setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось обновить этап');
        return;
      }
      navigate(listPath, { replace: true });
      return;
    }

    const res = await requestWithMeta(
      '/operator/custom-order-statuses',
      'POST',
      { name: trimmedName }
    );
    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось создать этап');
      return;
    }

    navigate(listPath, { replace: true });
  }

  return (
    <div className="shop-create-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title={isEdit ? 'Редактировать этап' : 'Новый этап'}
          subtitle={
            isEdit
              ? 'Измените название этапа заказа'
              : 'Укажите название этапа заказа'
          }
          onBack={() => navigate(listPath)}
        />

        <main className="shop-create-content">
          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          <section className="shop-create-section">
            {!isEdit ? (
              <div className="custom-status-intro">
                <p className="custom-status-introText">
                  Этап появится между «принят» и «выполнено».
                </p>
                <ol className="custom-status-flow">
                  {STATUS_FLOW.map((step, index) => (
                    <li
                      key={step.key}
                      className={`custom-status-flowStep custom-status-flowStep--${step.key}`}
                    >
                      <span className="custom-status-flowRail" aria-hidden="true">
                        <span className={`custom-status-flowDot custom-status-flowDot--${step.key}`} />
                        {index < STATUS_FLOW.length - 1 ? (
                          <span className="custom-status-flowLine" />
                        ) : null}
                      </span>
                      <span className="custom-status-flowLabel">{step.label}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
            <label className="shop-create-label" htmlFor="custom-order-status-name">
              Название <b style={{ color: 'red' }}>*</b>
            </label>
            <input
              id="custom-order-status-name"
              className="shop-create-input"
              placeholder="например, готовится, в доставке, собирается"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              disabled={isPrefillLoading}
            />
          </section>
        </main>

        <CreateFooter
          disabled={isSubmitDisabled}
          label={
            loading
              ? isEdit
                ? 'Сохранение…'
                : 'Создание…'
              : isEdit
                ? 'Сохранить'
                : 'Создать этап'
          }
          onCreate={handleSubmit}
        />
      </div>
    </div>
  );
}

export default UpsertCustomOrderStatus;
